import { useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { CheckCircleRounded, PaidOutlined } from "@mui/icons-material";
import CoordinatorAvatars from "../_components/CoordinatorAvatars";
import MoneyBars from "./MoneyBars";
import { clientLabel, dateRangeLabel, endedLabel, moneyColor, NEON, placeLabel, THEME } from "./boardStyles";
import type { BoardShow } from "./boardTypes";

const COLLAPSED_COUNT = 6;
const COLLAPSED_COUNT_PHONE = 3;

type Props = {
  shows: BoardShow[];
  loading: boolean;
  /** Shows the viewer highlighted: neon border. */
  highlightIds?: Set<string>;
  onOpen: (show: BoardShow) => void;
};

/**
 * "To close" (admins): approved shows of the last 120 days that are over but
 * not finalized — final invoice sent, client paid 100% and every PO paid.
 * Late ones first (invoice not sent 3 days after the show, client past due,
 * crew not paid 30 days after). All computed by the API.
 */
export default function ClosingStrip({ shows, loading, highlightIds, onOpen }: Props) {
  const [expanded, setExpanded] = useState(false);
  const phone = useMediaQuery("(max-width: 600px)");
  const collapsedCount = phone ? COLLAPSED_COUNT_PHONE : COLLAPSED_COUNT;
  const visible = expanded ? shows : shows.slice(0, collapsedCount);
  const late = shows.filter((s) => s.money?.issues.length).length;

  return (
    <Box component="section" aria-label="Shows to close">
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "14px", mb: "14px", flexWrap: "wrap" }}>
        <Typography variant="h4" component="h2">
          To close
        </Typography>
        <Typography sx={{ fontSize: "15px", opacity: 0.8 }}>
          {shows.length
            ? `${shows.length} finished show${shows.length === 1 ? "" : "s"} not paid in full${
                late ? ` · ${late} late` : ""
              }`
            : "Last 120 days"}
        </Typography>
      </Box>

      {!shows.length && !loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "16px 20px",
            borderRadius: "12px",
            border: `1px solid ${THEME.green}`,
          }}
        >
          <CheckCircleRounded sx={{ color: THEME.green }} />
          <Typography>Every finished show of the last 120 days is closed.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "16px" }}>
          {visible.map((show) => (
            <ClosingCard key={show._id} show={show} highlighted={!!highlightIds?.has(show._id)} onOpen={onOpen} />
          ))}
        </Box>
      )}

      {shows.length > collapsedCount ? (
        <Button size="small" onClick={() => setExpanded((v) => !v)} sx={{ mt: "14px", minWidth: "auto", px: 2, py: "4px" }}>
          {expanded ? "Show less" : `Show all ${shows.length}`}
        </Button>
      ) : null}
    </Box>
  );
}

function ClosingCard({
  show,
  highlighted,
  onOpen,
}: {
  show: BoardShow;
  highlighted: boolean;
  onOpen: (s: BoardShow) => void;
}) {
  const color = moneyColor(show);
  const subtitle = [clientLabel(show), placeLabel(show)].filter(Boolean).join(" · ");
  const reasons = show.money?.reasons ?? [];
  const issues = show.money?.issues.length ?? 0;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(show)}
      aria-label={`${show.eventName}: ${endedLabel(show)}, ${reasons.join(", ")}`}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        minWidth: 0,
        padding: "14px 16px",
        borderRadius: "12px",
        background: THEME.black,
        border: `1px solid ${highlighted ? NEON.color : THEME.pinkSoft}`,
        borderLeft: `6px solid ${color}`,
        boxShadow: highlighted ? NEON.glow : "none",
        transition: "box-shadow 150ms ease, border-color 150ms ease",
        "&:hover": {
          boxShadow: highlighted ? NEON.glow : THEME.glow,
          borderColor: highlighted ? NEON.color : THEME.pink,
          borderLeftColor: color,
        },
        "&:focus-visible": { outline: `2px solid ${THEME.pink}`, outlineOffset: "2px" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <PaidOutlined sx={{ fontSize: 20, color }} />
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color, whiteSpace: "nowrap" }}>{endedLabel(show)}</Typography>
          <Typography sx={{ fontSize: "14px", opacity: 0.7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            · {dateRangeLabel(show)}
          </Typography>
        </Box>
        <CoordinatorAvatars coordinators={show.coordinators} slots={3} size={26} disableTooltips />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          title={show.eventName}
          sx={{ fontSize: "18px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {show.eventName}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: "14px", opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      {reasons.length ? (
        <Box component="ul" sx={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {reasons.map((r, i) => (
            <Box
              component="li"
              key={r}
              sx={{
                fontSize: "13px",
                lineHeight: "20px",
                padding: "1px 8px",
                borderRadius: "6px",
                color: THEME.white,
                border: `1px solid ${i < issues ? "#FD355A" : THEME.pinkSoft}`,
              }}
            >
              {r}
            </Box>
          ))}
        </Box>
      ) : null}

      {show.money ? <MoneyBars value={show.money} /> : null}
    </Box>
  );
}
